import "./style.scss";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Collapse, Skeleton } from "@mui/material";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { formatLocaleDateString } from "helpers/utils";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IIntegrations } from "store/integrationsStore";
import { useAppSelector } from "store/store";

import SpreadSheetRow from "../SpreadSheetRow";

interface ISearchSheetBlock {
    currentIntegration: IIntegrations;
    mode: "edit" | "new";
}

/**
 * This component render the searching Google Sheets feature of the statement form.
 * It's used in the new statement form step 1 and in the edit statement form in the modal to change of template.
 */
const SearchSheetBlock = ({ currentIntegration, mode }: ISearchSheetBlock) => {
    const [t] = useTranslation();
    const { collapsedSpreadsheet, googleSpreadSheets, googleSheets, fetchGoogleSpreadsheetStatus } = useAppSelector(
        (state) => state.partnerStatements,
    );
    const { control, watch } = useFormContext<INewStatementForm>();
    const isCollapsedSpreadsheet = !!collapsedSpreadsheet.id;

    const searchSpreadsheetQuery = watch("searchSpreadsheet")?.toLowerCase();
    const searchSheetQuery = watch("searchSheet")?.toLowerCase();

    /**
     * Switch the control input name if we have to search Spreadsheets or sheets
     */
    const searchInputControlName = useMemo(() => {
        if (isCollapsedSpreadsheet) return "searchSheet";
        else return "searchSpreadsheet";
    }, [isCollapsedSpreadsheet]);

    /**
     * Filter Google Spreadsheets
     */
    const filteredSpreadsheets = useMemo(() => {
        if (isCollapsedSpreadsheet)
            return [googleSpreadSheets.find((spreadsheet: any) => spreadsheet.id === collapsedSpreadsheet.id)];
        if (searchSpreadsheetQuery)
            return googleSpreadSheets.filter((spreadsheet: any) =>
                spreadsheet.name.toLowerCase().includes(searchSpreadsheetQuery),
            );
        else return googleSpreadSheets;
    }, [collapsedSpreadsheet, googleSpreadSheets, searchSpreadsheetQuery]);

    /**
     * Filter Sheets inside Spreadsheets
     */
    const filteredSheets = useMemo(() => {
        if (searchSheetQuery)
            return googleSheets.filter((sheet: any) => sheet.properties.title.toLowerCase().includes(searchSheetQuery));
        else return googleSheets;
    }, [searchSheetQuery, googleSheets]);

    const { paginatedData: paginatedSpreadSheets, paginatedElRef } = useInfiniteScroll(filteredSpreadsheets, true);

    return (
        <div className="search-sheet-block">
            {fetchGoogleSpreadsheetStatus === "pending" && (
                <div>
                    <Skeleton width="60%" />
                    <Skeleton />
                    <Skeleton />
                </div>
            )}
            <Collapse in={googleSpreadSheets.length > 0} timeout="auto">
                <div className="spreadsheet-search">
                    <TextFormComponent
                        control={control}
                        controlName={searchInputControlName}
                        label={t("PartnerStatements.ChooseStatement")}
                        placeholder={t("PartnerStatements.Search")}
                        size="small"
                    />
                    <SearchOutlinedIcon />
                </div>
                <ScrollShadowWrapper className="new-statement-spreadsheets-list" ref={paginatedElRef}>
                    {paginatedSpreadSheets?.map((spreadsheet: any, index) => (
                        <SpreadSheetRow
                            key={`spreadsheet-row-${spreadsheet.id}`}
                            spreadsheetName={spreadsheet?.name}
                            spreadsheetId={spreadsheet.id}
                            integrationId={currentIntegration?.id as number}
                            filteredSheets={filteredSheets}
                            modifiedTime={formatLocaleDateString({
                                date: spreadsheet.modifiedTime,
                                options: { numeric: true, timeZone: "UTC" },
                            })}
                            mode={mode}
                        />
                    ))}
                </ScrollShadowWrapper>
            </Collapse>
        </div>
    );
};

export default SearchSheetBlock;
